// src/hooks/use-toast.ts

// Inspired by react-hot-toast library
import * as React from "react";
import { Loader2 } from "lucide-react";

import type { ToastActionElement, ToastProps } from "../components/ui/toast";

const TOAST_LIMIT = 5;
const TOAST_REMOVE_DELAY = 1000000;

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  variant?: "default" | "success" | "error" | "warning" | "info";
  icon?: React.ReactNode;
  showProgress?: boolean;
  duration?: number;
};

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType["ADD_TOAST"];
      toast: ToasterToast;
    }
  | {
      type: ActionType["UPDATE_TOAST"];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType["DISMISS_TOAST"];
      toastId?: ToasterToast["id"];
    }
  | {
      type: ActionType["REMOVE_TOAST"];
      toastId?: ToasterToast["id"];
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string, duration?: number) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const removeDelay = duration || TOAST_REMOVE_DELAY;

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    });
  }, removeDelay);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      };

    case "DISMISS_TOAST": {
      const { toastId } = action;

      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t,
        ),
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

type Toast = Omit<ToasterToast, "id">;

function toast({ duration, ...props }: Toast) {
  const id = genId();

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      duration,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  if (duration !== undefined && duration > 0) {
    addToRemoveQueue(id, duration);
  }

  return {
    id: id,
    dismiss,
    update,
  };
}

// Create loading icon using React.createElement (works in .ts files)
const loadingIcon = React.createElement(Loader2, {
  className: "h-5 w-5 animate-spin",
});

toast.success = (
  title: string,
  description?: string,
  options?: Partial<Toast>,
) => {
  return toast({
    title,
    description,
    variant: "success",
    showProgress: true,
    duration: options?.duration || 3000,
    ...options,
  });
};

toast.error = (
  title: string,
  description?: string,
  options?: Partial<Toast>,
) => {
  return toast({
    title,
    description,
    variant: "error",
    showProgress: true,
    duration: options?.duration || 5000,
    ...options,
  });
};

toast.warning = (
  title: string,
  description?: string,
  options?: Partial<Toast>,
) => {
  return toast({
    title,
    description,
    variant: "warning",
    showProgress: true,
    duration: options?.duration || 4000,
    ...options,
  });
};

toast.info = (
  title: string,
  description?: string,
  options?: Partial<Toast>,
) => {
  return toast({
    title,
    description,
    variant: "info",
    showProgress: true,
    duration: options?.duration || 3000,
    ...options,
  });
};

toast.loading = (title: string, description?: string) => {
  return toast({
    title,
    description,
    icon: loadingIcon, // Use the pre-created element
    showProgress: false,
    duration: Infinity,
  });
};

toast.dismiss = (toastId?: string) => {
  dispatch({ type: "DISMISS_TOAST", toastId });
};

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  const success = React.useCallback(
    (title: string, description?: string, options?: Partial<Toast>) => {
      return toast.success(title, description, options);
    },
    [],
  );

  const error = React.useCallback(
    (title: string, description?: string, options?: Partial<Toast>) => {
      return toast.error(title, description, options);
    },
    [],
  );

  const warning = React.useCallback(
    (title: string, description?: string, options?: Partial<Toast>) => {
      return toast.warning(title, description, options);
    },
    [],
  );

  const info = React.useCallback(
    (title: string, description?: string, options?: Partial<Toast>) => {
      return toast.info(title, description, options);
    },
    [],
  );

  const loading = React.useCallback((title: string, description?: string) => {
    return toast.loading(title, description);
  }, []);

  const dismiss = React.useCallback((toastId?: string) => {
    toast.dismiss(toastId);
  }, []);

  return {
    ...state,
    toast,
    success,
    error,
    warning,
    info,
    loading,
    dismiss,
  };
}

export { useToast, toast };
